import { useEffect, useState } from "react";
import type { JSX } from "react";
import { useLocation, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { useAuth } from "./AuthProvider.js";
import { consumeResumeTarget } from "./resumeRedirect.js";
import { friendlyError } from "../../shared/http.js";
import { PASSWORD_MIN_LENGTH } from "@recipevault/shared";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/ui/form.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card.js";
import { Alert, AlertDescription } from "@/ui/alert.js";

// Login uses the minimum length only (no complexity rules) so existing accounts
// with passwords below the current policy can still attempt sign-in; the actual
// strength gate is on password CREATION (invite-accept + future password-reset).
const loginSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`),
});
type LoginInput = z.infer<typeof loginSchema>;

export function Login(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const stateFrom = (location.state as { from?: unknown } | null)?.from;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If the user is already authenticated (e.g., they refreshed /login while logged in),
  // resume them to wherever they were headed (or home, if nowhere in particular).
  useEffect(() => {
    if (auth.status === "authenticated") {
      void navigate(consumeResumeTarget(stateFrom), { replace: true });
    }
  }, [auth.status, navigate, stateFrom]);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
    mode: "onChange",
  });

  async function onSubmit(values: LoginInput): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      await auth.signIn(values.email, values.password);
      void navigate(consumeResumeTarget(stateFrom), { replace: true });
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Sign in to RecipeVault</CardTitle>
          <CardDescription>Use the email and password you set up.</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="email"
                        autoFocus
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="current-password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {error && (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || !form.formState.isValid}
              >
                {submitting && <Loader2 className="size-4 animate-spin" aria-hidden />}
                {submitting ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </main>
  );
}
